import { type ContactPhone } from "@/lib/types/Contact";

import {
  ActionConfirmationModal,
  Badge,
  Button,
  ContactCallSplitButton,
  Label,
  PhoneNumberFormField,
  SimpleSelectFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { useDeleteContactPhone } from "@/lib/data/react-query";
import { useState } from "react";
import { AddOrEditContactPhoneNumberSlideover } from "./AddOrEditContactPhoneNumberSlideover";

import { type createContactFormSchema } from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import { ContactFieldRowError } from "@/components/features/contact/ContactFieldRowError/ContactFieldRowError";
import { ContactPrimaryStarButton } from "@/components/features/contact/ContactPrimaryStarButton/ContactPrimaryStarButton";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { getContactPhoneLabelOptions } from "@/lib/data/static/contactStatics";
import pluralize from "pluralize";
import {
  useFieldArray,
  useWatch,
  type FieldPath,
  type UseFormReturn,
} from "react-hook-form";
import { type z } from "zod";

import { ContactPhoneIcon } from "@/lib/utils/iconUtils";
import { formatLocalOrIntlPhoneNumber } from "@/lib/utils/format";
import { CheckCircle2, Pencil, Plus, Trash2 } from "lucide-react";
import { type ContactType } from "@/lib/types/Contact";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";

const newClientRowId = (): string => {
  if (typeof globalThis.crypto.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

function ContactPhoneInlineFields({
  parentForm,
  hasCreatePermission,
  hasEditPermission,
  hasDeletePermission,
  hideLabel,
  organizationSubTypes,
  type,
}: {
  parentForm: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  hasCreatePermission?: boolean;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  hideLabel?: boolean;
  organizationSubTypes?: readonly string[];
  type?: ContactType;
}) {
  const { control, register, getValues, setFocus, setValue } = parentForm;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "phones",
  });
  const watchedPhones = useWatch({ control, name: "phones" });
  const labelOptions = getContactPhoneLabelOptions(type, organizationSubTypes);

  const setPrimary = (index: number) => {
    const n = (getValues("phones") ?? []).length;
    for (let i = 0; i < n; i++) {
      setValue(`phones.${i}.isPrimary`, i === index, {
        shouldDirty: true,
      });
    }
  };

  const handleRemove = (index: number) => {
    if (!hasDeletePermission) {
      return;
    }
    const wasPrimary = getValues(`phones.${index}.isPrimary`);
    remove(index);
    if (wasPrimary) {
      const next = getValues("phones") ?? [];
      if (next.length > 0) {
        for (let i = 0; i < next.length; i++) {
          setValue(`phones.${i}.isPrimary`, i === 0, {
            shouldDirty: true,
          });
        }
      }
    }
  };

  const addRow = () => {
    if (!hasCreatePermission) {
      return;
    }
    const list = getValues("phones") ?? [];
    append({
      id: newClientRowId(),
      number: "",
      extension: "",
      label: labelOptions[0] ?? "Home",
      description: "",
      isPrimary: list.length === 0,
      dateModified: "",
    });
    requestAnimationFrame(() => {
      setFocus(`phones.${list.length}.number`);
    });
  };

  const phoneCount = Math.max(fields.length, 1);
  const disabled = !hasEditPermission;
  const phoneListLen = fields.length;

  return (
    <div>
      {!hideLabel && (
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center space-x-2">
            <ContactPhoneIcon className="h-5 w-5 text-gray-400" />
            <Label className="text-lg font-semibold">
              {pluralize("Phone", phoneCount)}
            </Label>
          </div>
        </div>
      )}
      <div className="col-span-full overflow-x-visible">
        {phoneListLen > 0 && (
          <ul className="space-y-2">
            {fields.map((field, index) => {
              const row = watchedPhones?.[index];
              const isPrimary = !!row?.isPrimary;
              return (
                <li
                  key={field.id}
                  className="flex flex-col gap-1"
                  data-testid={
                    row?.id ? `contact-phone-item-${row.id}` : undefined
                  }
                >
                  <div className="flex min-w-0 items-stretch gap-2 sm:gap-3">
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col justify-center">
                      <PhoneNumberFormField
                        className="w-full !space-y-0 [&>div:first-child]:hidden"
                        register={register(`phones.${index}.number`)}
                        disabled={disabled}
                        dataTestId="contact-phone-number-input"
                      />
                    </div>
                    <div className="flex w-20 shrink-0 flex-col justify-center self-stretch sm:w-24">
                      <TextFormField
                        className="w-full !space-y-0 [&>div:first-child]:hidden"
                        register={register(`phones.${index}.extension`)}
                        placeholder="Ext"
                        disabled={disabled}
                      />
                    </div>
                    <div className="flex w-32 min-w-0 flex-col justify-center self-stretch sm:w-36">
                      <SimpleSelectFormField
                        className="h-10 w-full min-w-0"
                        formItemClassName="!m-0 w-full min-w-0 !space-y-0"
                        placeholder="Label"
                        register={register(`phones.${index}.label`)}
                        items={labelOptions as readonly string[]}
                        disabled={disabled}
                      />
                    </div>
                    <div className="flex shrink-0 flex-col justify-center self-stretch">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <ContactPrimaryStarButton
                          isPrimary={isPrimary}
                          disabled={disabled}
                          onClick={() => {
                            setPrimary(index);
                          }}
                          className="h-10 w-10"
                          ariaLabelCurrent="Primary phone"
                          ariaLabelSet="Set as primary phone"
                        />
                        {hasDeletePermission && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 shrink-0"
                            disabled={disabled}
                            onClick={() => {
                              handleRemove(index);
                            }}
                            aria-label="Remove phone number"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  <ContactFieldRowError
                    control={parentForm.control}
                    fieldPath={
                      `phones.${index}.number` as FieldPath<
                        z.infer<typeof createContactFormSchema>
                      >
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
        {hasCreatePermission && (
          <div className="mt-2 w-full self-start">
            <Button
              type="button"
              variant="link"
              className="contact-subitem-add text-muted-foreground h-auto min-h-0 justify-start p-0"
              onClick={addRow}
            >
              <Plus className="h-4 w-4 shrink-0" />
              Add phone
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ContactPhoneNumbersSection({
  phoneNumbers,
  contactId,
  hasCreatePermission,
  hasEditPermission,
  hasDeletePermission,
  hideLabel,
  parentForm,
  organizationSubTypes,
  type,
}: {
  parentForm?: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  phoneNumbers?: ContactPhone[];
  contactId: string;
  hasCreatePermission?: boolean;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  hideLabel?: boolean;
  organizationSubTypes?: readonly string[];
  type?: ContactType;
}) {
  const hasViewTextPermission = true;
  const deleteContactPhoneNumber = useDeleteContactPhone();
  const [activePhoneId, setActivePhoneId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const { openCommunicationModal } = useGlobalModal();

  if (parentForm) {
    return (
      <ContactPhoneInlineFields
        parentForm={parentForm}
        hasCreatePermission={hasCreatePermission}
        hasEditPermission={hasEditPermission}
        hasDeletePermission={hasDeletePermission}
        hideLabel={hideLabel}
        organizationSubTypes={organizationSubTypes}
        type={type}
      />
    );
  }

  const phoneList = phoneNumbers ?? [];
  const parseSortableDate = (value: unknown) =>
    typeof value === "string" ? Date.parse(value) : Number.NaN;
  const sortedPhoneList = phoneList
    .map((phone, index) => ({ phone, index }))
    .sort((a, b) => {
      const aPrimary = "isPrimary" in a.phone ? !!a.phone.isPrimary : false;
      const bPrimary = "isPrimary" in b.phone ? !!b.phone.isPrimary : false;
      if (aPrimary !== bPrimary) {
        return aPrimary ? -1 : 1;
      }
      const aDate = parseSortableDate(a.phone.dateModified);
      const bDate = parseSortableDate(b.phone.dateModified);
      if (!Number.isNaN(aDate) && !Number.isNaN(bDate) && aDate !== bDate) {
        return aDate - bDate;
      }
      return a.index - b.index;
    })
    .map(({ phone }) => phone);
  const phoneCount = Math.max(sortedPhoneList.length, 1);
  const showEmptyState = phoneList.length === 0 && !isAdding;
  const activePhone = (phoneNumbers ?? []).find(
    (phoneNumber) => phoneNumber.id === activePhoneId,
  );

  const handleDelete = (phoneNumber: Pick<ContactPhone, "id">) => {
    deleteContactPhoneNumber.mutate({
      id: phoneNumber.id,
      contactId: contactId,
    });
    setActivePhoneId(null);
  };

  return (
    <div>
      {!hideLabel && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center space-x-2">
            <ContactPhoneIcon className="h-5 w-5 text-gray-400" />
            <Label className="text-lg font-semibold">
              {pluralize("Phone", phoneCount)}
            </Label>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <TooltipWrapper
              message={
                phoneList.length === 0 &&
                "Add a phone number to send a text message."
              }
            >
              <div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    openCommunicationModal({
                      contactId: contactId,
                      setSelectedContactId: () => {},
                    });
                  }}
                  disabled={phoneList.length === 0 || !hasViewTextPermission}
                >
                  Send text
                </Button>
              </div>
            </TooltipWrapper>
            <ContactCallSplitButton
              contactId={contactId}
              phones={sortedPhoneList}
            />
            {hasCreatePermission && (
              <Button
                type="button"
                onClick={() => {
                  setIsAdding(true);
                }}
              >
                Create phone number
              </Button>
            )}
          </div>
        </div>
      )}
      <div className="col-span-full">
        {hideLabel && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <TooltipWrapper
              message={
                phoneList.length === 0 &&
                "Add a phone number to send a text message."
              }
            >
              <div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    openCommunicationModal({
                      contactId: contactId,
                      setSelectedContactId: () => {},
                    });
                  }}
                  disabled={phoneList.length === 0 || !hasViewTextPermission}
                >
                  Send text
                </Button>
              </div>
            </TooltipWrapper>
            <ContactCallSplitButton
              contactId={contactId}
              phones={sortedPhoneList}
            />
            {hasCreatePermission && (
              <Button
                type="button"
                onClick={() => {
                  setIsAdding(true);
                }}
              >
                Create phone number
              </Button>
            )}
          </div>
        )}
        {isAdding && (
          <div className="border-border bg-card mt-3 w-full rounded-md border p-3">
            <AddOrEditContactPhoneNumberSlideover
              parentForm={parentForm}
              editing={false}
              setOpen={setIsAdding}
              open={isAdding}
              contactId={contactId}
              hasEditPermission={hasEditPermission}
              organizationSubTypes={organizationSubTypes}
              type={type}
              variant="inline"
            />
          </div>
        )}
        {showEmptyState ? (
          <Typography
            variant="subHeading"
            className="text-muted-foreground mt-3"
          >
            No phone numbers found.
          </Typography>
        ) : (
          <ul className="mt-3 space-y-3">
            {sortedPhoneList.map((phone, index) => {
              const isPrimary =
                "isPrimary" in phone ? phone.isPrimary : undefined;
              const label = phone.label?.trim();
              const isActiveEdit = isEditing && phone.id === activePhoneId;
              if (isActiveEdit) {
                return (
                  <li
                    key={phone.id || `${phone.number}-${index}`}
                    className="border-border bg-card w-full rounded-md border p-3"
                  >
                    <AddOrEditContactPhoneNumberSlideover
                      editing={true}
                      parentForm={parentForm}
                      activeContactPhoneNumber={phone}
                      setOpen={(nextOpen) => {
                        setIsEditing(nextOpen);
                        if (!nextOpen) {
                          setActivePhoneId(null);
                        }
                      }}
                      open={isEditing}
                      contactId={contactId}
                      hasEditPermission={hasEditPermission}
                      hasDeletePermission={hasDeletePermission}
                      onDelete={() => {
                        setIsDeleting(true);
                      }}
                      organizationSubTypes={organizationSubTypes}
                      type={type}
                      variant="inline"
                    />
                  </li>
                );
              }
              return (
                <li
                  key={phone.id || `${phone.number}-${index}`}
                  className="border-border bg-card w-full rounded-md border p-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <Typography weight="medium" className="truncate">
                        {formatLocalOrIntlPhoneNumber(phone) || "-"}
                      </Typography>
                      {label && (
                        <Badge variant="secondary" className="h-6 shrink-0">
                          {label}
                        </Badge>
                      )}
                      {Boolean(isPrimary) && (
                        <TooltipWrapper message="Primary">
                          <span className="flex items-center">
                            <CheckCircle2
                              className="h-4 w-4 text-emerald-600"
                              aria-hidden="true"
                            />
                            <span className="sr-only">Primary</span>
                          </span>
                        </TooltipWrapper>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (!phone.id) {
                          return;
                        }
                        setIsEditing(true);
                        setActivePhoneId(phone.id);
                      }}
                      disabled={!phone.id}
                      aria-label="Edit phone number"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <ActionConfirmationModal
        title={`Are you sure you want to delete this phone?`}
        open={isDeleting && !!activePhone}
        setOpen={setIsDeleting}
        afterActionFn={() => {
          if (activePhone) {
            handleDelete(activePhone);
          }
        }}
      />
    </div>
  );
}
